import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Star, ExternalLink, Loader2 } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

export interface AmazonCardItem {
  asin: string;
  title: string;
  brand: string | null;
  image: string | null;
  price_display: string | null;
  was_price_display: string | null;
  savings_display: string | null;
  savings_percentage: number | null;
  availability: string | null;
  rating: number | null;
  review_count: number | null;
  detail_url: string;
}

interface Props {
  asin: string;
  variant?: "card" | "compact";
  item?: AmazonCardItem | null;
}

export default function AmazonProductCard({ asin, variant = "card", item }: Props) {
  const cleanAsin = (asin || "").trim().toUpperCase();
  const valid = /^[A-Z0-9]{10}$/.test(cleanAsin);
  const hasProvidedItem = item !== undefined;

  const { data: fetchedData, isLoading, isError } = useQuery({
    queryKey: ["amazon-card", cleanAsin],
    enabled: valid && !hasProvidedItem,
    staleTime: 60 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("amazon-get-items", {
        body: { asins: [cleanAsin] },
      });
      if (error) throw error;
      return (data?.items?.[cleanAsin] ?? null) as AmazonCardItem | null;
    },
  });

  const data = hasProvidedItem ? item : fetchedData;
  // API records can contain a bare ASIN rather than an absolute shop URL.
  // Never resolve that relative to the current article.
  const amazonUrl = `https://www.amazon.co.uk/dp/${cleanAsin}?tag=${import.meta.env.VITE_AMAZON_PARTNER_TAG || "mediam07b-21"}`;
  const detailUrl = (() => {
    try {
      const url = new URL(data?.detail_url || "");
      if (url.protocol === "https:" && (url.hostname === "amazon.co.uk" || url.hostname.endsWith(".amazon.co.uk"))) return url.href;
    } catch { /* use the product's canonical Amazon URL */ }
    return amazonUrl;
  })();

  if (!valid) return null;

  if (isLoading) {
    return (
      <div className="not-prose my-6 rounded-lg border border-border bg-card p-4 flex gap-4 items-center">
        <Skeleton className="h-24 w-24 rounded" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-1/3" />
          <Skeleton className="h-9 w-32" />
        </div>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="not-prose my-6 rounded-lg border border-border bg-muted/30 p-4 text-sm text-muted-foreground">
        Product temporarily unavailable.{" "}
        <a
          href={amazonUrl}
          target="_blank"
          rel="noopener noreferrer sponsored"
          className="text-primary underline"
        >
          View on Amazon
        </a>
      </div>
    );
  }

  return (
    <aside
      className="not-prose my-6 rounded-xl border border-border bg-card shadow-soft overflow-hidden"
      aria-label={`Amazon product: ${data.title}`}
    >
      <div className="flex flex-col sm:flex-row gap-4 p-4">
        {data.image && (
          <a
            href={detailUrl}
            target="_blank"
            rel="noopener noreferrer sponsored"
            className="shrink-0 mx-auto sm:mx-0"
          >
            <img
              src={data.image}
              alt={data.title}
              loading="lazy"
              className="!h-32 !w-32 !max-h-32 !my-0 object-contain rounded bg-white"
            />
          </a>
        )}
        <div className="flex-1 min-w-0 flex flex-col">
          {data.brand && (
            <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
              {data.brand}
            </div>
          )}
          <a
            href={detailUrl}
            target="_blank"
            rel="noopener noreferrer sponsored"
            className="font-semibold text-foreground hover:text-primary leading-snug line-clamp-3"
          >
            {data.title}
          </a>

          {(data.rating || data.review_count) && (
            <div className="flex items-center gap-1.5 mt-2 text-sm">
              {data.rating && (
                <span className="flex items-center gap-0.5">
                  <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                  <span className="font-medium">{Number(data.rating).toFixed(1)}</span>
                </span>
              )}
              {data.review_count && (
                <span className="text-muted-foreground">
                  ({data.review_count.toLocaleString()})
                </span>
              )}
            </div>
          )}

          <div className="flex flex-wrap items-end justify-between gap-3 mt-3">
            <div>
              {data.price_display && (
                <div className="flex items-baseline flex-wrap gap-2 leading-none">
                  <span className="text-2xl font-bold text-red-600">
                    {data.price_display}
                  </span>
                  {data.was_price_display && (
                    <span className="text-sm text-muted-foreground line-through">
                      {data.was_price_display}
                    </span>
                  )}
                  {(data.savings_percentage || data.savings_display) && (
                    <span className="inline-flex items-center rounded bg-red-600 px-1.5 py-0.5 text-xs font-bold text-white">
                      {data.savings_percentage
                        ? `-${data.savings_percentage}%`
                        : `Save ${data.savings_display}`}
                    </span>
                  )}
                </div>
              )}
              {data.savings_display && data.savings_percentage && (
                <div className="text-xs font-medium text-red-600 mt-1">
                  You save {data.savings_display}
                </div>
              )}
              {data.availability && (
                <div className="text-xs text-muted-foreground mt-1">{data.availability}</div>
              )}
            </div>
            <a
              href={detailUrl}
              target="_blank"
              rel="noopener noreferrer sponsored"
              className="inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-semibold text-white shadow-sm"
              style={{ backgroundColor: "#16a34a" }}
            >
              Check price on Amazon
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </div>
          <div className="text-[10px] text-muted-foreground mt-2">
            As an Amazon Associate we earn from qualifying purchases. Price &amp; availability accurate at time of fetch.
          </div>
        </div>
      </div>
    </aside>
  );
}
